import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as openpgp from 'openpgp';

const Inbox = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [pgpKeys, setPgpKeys] = useState([]);
  const [decryptedContent, setDecryptedContent] = useState(null);
  const [decrypting, setDecrypting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, encrypted
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmails();
    fetchPgpKeys();
  }, []);

  const fetchEmails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/emails/inbox', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmails(response.data.emails || []);
    } catch (err) {
      console.error('Failed to fetch emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPgpKeys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/pgp/keys', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPgpKeys(response.data.keys || []);
    } catch (err) {
      console.error('Failed to fetch PGP keys:', err);
    }
  };

  const handleDecrypt = async (email) => {
    if (!email.is_encrypted) return;
    
    setDecrypting(true);
    setDecryptedContent(null);

    try {
      // Try each private key until one works
      let decrypted = null;
      let signatureValid = null;

      for (const key of pgpKeys) {
        if (!key.private_key) continue;

        try {
          const privateKey = await openpgp.readPrivateKey({
            armoredKey: key.private_key
          });

          const message = await openpgp.readMessage({
            armoredMessage: email.body
          });

          const result = await openpgp.decrypt({
            message,
            decryptionKeys: privateKey
          });

          decrypted = result.data;
          signatureValid = result.signatures?.[0]?.verified || null;
          break;
        } catch (e) {
          continue;
        }
      }

      if (decrypted) {
        setDecryptedContent({
          body: decrypted,
          signatureValid
        });
      } else {
        alert('Could not decrypt with any available private key');
      }
    } catch (err) {
      console.error('Decryption error:', err);
      alert('Failed to decrypt: ' + err.message);
    } finally {
      setDecrypting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredEmails = emails.filter(email => {
    // Filter by type
    if (filter === 'unread' && email.is_read) return false;
    if (filter === 'encrypted' && !email.is_encrypted) return false;
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        email.subject?.toLowerCase().includes(query) ||
        email.from_address?.toLowerCase().includes(query) ||
        email.body?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const EmailDetail = ({ email, onClose }) => {
    if (!email) return null;

    return (
      <div className="card" style={{ 
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        zIndex: 1000,
        padding: '24px'
      }}>
        {/* Backdrop */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: -1
          }}
          onClick={onClose}
        />

        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid var(--border)'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              {email.is_encrypted && (
                <span style={{ 
                  background: '#8B5CF6',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  🔐 ENCRYPTED
                </span>
              )}
              {email.pgp_signed && (
                <span style={{ 
                  background: '#10B981',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  ✓ SIGNED
                </span>
              )}
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '8px' }}>
              {email.subject}
            </h2>
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              <div>From: <strong style={{ color: 'var(--text)' }}>{email.from_address}</strong></div>
              <div>To: {email.to_address}</div>
              <div>{new Date(email.created_at).toLocaleString()}</div>
            </div>
          </div>
          <button 
            className="btn btn-ghost"
            onClick={onClose}
            style={{ fontSize: '20px' }}
          >
            ✕
          </button>
        </div>

        {/* Email Body */}
        <div style={{ marginBottom: '20px' }}>
          {email.is_encrypted && !decryptedContent ? (
            <div style={{
              background: 'var(--darker)',
              border: '1px dashed var(--border)',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔐</div>
              <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
                This email is encrypted with PGP
              </p>
              <button
                className="btn btn-primary"
                onClick={() => handleDecrypt(email)}
                disabled={decrypting}
              >
                {decrypting ? '🔓 Decrypting...' : '🔓 Decrypt Message'}
              </button>
            </div>
          ) : decryptedContent ? (
            <div>
              {decryptedContent.signatureValid !== null && (
                <div style={{
                  background: decryptedContent.signatureValid ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${decryptedContent.signatureValid ? '#10B981' : '#EF4444'}`,
                  borderRadius: '6px',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{decryptedContent.signatureValid ? '✓' : '⚠️'}</span>
                  <span style={{ color: decryptedContent.signatureValid ? '#10B981' : '#EF4444' }}>
                    {decryptedContent.signatureValid ? 'Signature verified' : 'Invalid signature'}
                  </span>
                </div>
              )}
              <div style={{
                background: 'var(--darker)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '20px',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6'
              }}>
                {decryptedContent.body}
              </div>
            </div>
          ) : (
            <div style={{
              background: 'var(--darker)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '20px',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6'
            }}>
              {email.body}
            </div>
          )}
        </div>

        {/* Attachments */}
        {email.attachments && email.attachments.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-muted)' }}>
              📎 Attachments ({email.attachments.length})
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {email.attachments.map((att, idx) => (
                <div 
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    background: 'var(--darker)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px'
                  }}
                >
                  <span>📄</span>
                  <span style={{ fontSize: '13px' }}>{att.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border)'
        }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/compose', { state: { replyTo: email } })}
          >
            ↩️ Reply
          </button>
          <button 
            className="btn btn-ghost"
            onClick={() => {}}
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700' }}>📬 Inbox</h1>
          <p style={{ color: 'var(--text-muted)' }}>
            {emails.filter(e => !e.is_read).length} unread messages
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/compose')}
        >
          ✉️ New Message
        </button>
      </div>

      {/* Search & Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search emails..."
            style={{
              width: '100%',
              padding: '10px 14px',
              background: 'var(--darker)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text)'
            }}
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: '10px 14px',
            background: 'var(--darker)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            color: 'var(--text)'
          }}
        >
          <option value="all">All Messages</option>
          <option value="unread">Unread</option>
          <option value="encrypted">Encrypted</option>
        </select>
      </div>

      {/* Email List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="loading"></div>
          <p style={{ color: 'var(--text-muted)', marginTop: '16px' }}>
            Loading messages...
          </p>
        </div>
      ) : filteredEmails.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <h3 style={{ marginBottom: '8px' }}>No messages</h3>
          <p style={{ color: 'var(--text-muted)' }}>
            {filter === 'encrypted' 
              ? 'No encrypted messages found' 
              : 'Your inbox is empty'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredEmails.map((email) => (
            <div
              key={email.id}
              onClick={() => {
                setSelectedEmail(email);
                setDecryptedContent(null);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px 20px',
                background: email.is_read ? 'var(--card)' : 'var(--darker)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: email.is_read ? 0.8 : 1
              }}
              className="hover-lift"
            >
              {/* Unread Indicator */}
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: email.is_read ? 'transparent' : '#DC2626',
                flexShrink: 0
              }} />

              {/* Icons */}
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {email.is_encrypted && <span title="Encrypted">🔐</span>}
                {email.pgp_signed && <span title="Signed">✓</span>}
                {email.attachments?.length > 0 && <span title="Attachments">📎</span>}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <span style={{ 
                    fontWeight: email.is_read ? '400' : '600',
                    color: 'var(--text)'
                  }}>
                    {email.from_address}
                  </span>
                  <span style={{ 
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    flexShrink: 0
                  }}>
                    {formatDate(email.created_at)}
                  </span>
                </div>
                <div style={{
                  fontWeight: email.is_read ? '400' : '600',
                  marginBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {email.subject}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {email.is_encrypted ? '🔐 Encrypted message' : email.body?.slice(0, 80)}...
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <EmailDetail 
          email={selectedEmail} 
          onClose={() => {
            setSelectedEmail(null);
            setDecryptedContent(null);
          }}
        />
      )}
    </div>
  );
};

export default Inbox;

